import React from 'react';
import {
  Paper, Box, Typography, Grid, TextField, MenuItem, Autocomplete, InputAdornment, Button, Divider, Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { AddShoppingCart as AddShoppingCartIcon, Pets as PetsIcon, LocalOfferOutlined as TagIcon } from '@mui/icons-material';

const AlbaranItemSelector = ({
  options,
  selectedItem,
  setSelectedItem,
  onSearchCatalogo,
  loadingBusqueda,
  mascotasCliente,
  itemForm,
  setItemForm,
  selectedPaciente,
  esEditable,
  preciosDisponibles,
  onPrecioChange,
  onCantidadChange,
  onPorcentajeDescChange,
  onMontoDescChange,
  onAgregarItem,
}) => {
  const theme = useTheme();

  const cant = parseFloat(itemForm.cantidad) || 0;
  const precio = parseFloat(itemForm.precio_unitario) || 0;
  const montoDescuento = parseFloat(itemForm.monto_descuento) || 0;
  const subtotalLinea = Math.max(0, cant * precio - montoDescuento);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        bgcolor: 'background.paper',
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
      }}
    >
      {/* Header del Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
            }}
          >
            <AddShoppingCartIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              Añadir Artículo o Servicio
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecciona el producto, asigna paciente y ajusta importes
            </Typography>
          </Box>
        </Box>

        {selectedItem && (
          <Chip
            size="small"
            icon={<TagIcon style={{ fontSize: 14 }} />}
            label={`${selectedItem.referencia ? `[${selectedItem.referencia}] ` : ''}${selectedItem.descripcion}`}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              fontWeight: 600,
              border: 'none',
              maxWidth: 260,
            }}
          />
        )}
      </Box>

      {/* Fila 1: Producto + Paciente */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={7}>
          <Autocomplete
            options={options}
            value={selectedItem}
            getOptionLabel={(opt) => `${opt.referencia ? `[${opt.referencia}] ` : ''}${opt.descripcion}`}
            onInputChange={(_, value) => onSearchCatalogo(value, false)}
            onChange={(_, value) => setSelectedItem(value)}
            loading={loadingBusqueda}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Buscar por Nombre / Código" 
                placeholder="Escanee o escriba..." 
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            select
            fullWidth
            size="small"
            label="Mascota / Paciente"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PetsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            value={
              mascotasCliente.some(
                (p) => String(p.id_paciente) === String(itemForm.id_paciente || selectedPaciente?.id_paciente)
              )
                ? (itemForm.id_paciente || selectedPaciente?.id_paciente)
                : ''
            }
            onChange={(e) => setItemForm((prev) => ({ ...prev, id_paciente: e.target.value }))}
            disabled={!esEditable || mascotasCliente.length === 0}
          >
            {mascotasCliente.map((p) => (
              <MenuItem key={p.id_paciente} value={p.id_paciente}>
                {p.nombre_paciente || p.nombre} {p.nombre_raza ? `(${p.nombre_raza})` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

      {/* Fila 2: Cálculo Monetario */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6} md={1.5}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Cantidad"
            value={itemForm.cantidad}
            onChange={(e) => onCantidadChange(e.target.value)}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Precio Unitario"
            value={selectedItem && preciosDisponibles.length > 0 ? itemForm.precio_unitario : ''}
            onChange={(e) => onPrecioChange(e.target.value)}
            disabled={!selectedItem || preciosDisponibles.length === 0}
          >
            {preciosDisponibles.map((p, idx) => (
              <MenuItem key={p.id_precio || idx} value={p.precio}>
                ${parseFloat(p.precio).toFixed(2)} {p.nombre_lista ? `(${p.nombre_lista})` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} md={1.75}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Desc. (%)"
            value={itemForm.porcentaje_descuento}
            onChange={(e) => onPorcentajeDescChange(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>

        <Grid item xs={6} md={1.75}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Desc. ($)"
            value={itemForm.monto_descuento}
            onChange={(e) => onMontoDescChange(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
        </Grid>

        {/* Subtotal estilo KPI */}
        <Grid item xs={12} md={2}>
          <Paper
            elevation={0}
            sx={{
              p: 1,
              px: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>
              Subtotal Línea
            </Typography>
            <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ lineHeight: 1.1 }}>
              ${subtotalLinea.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={!selectedItem}
            onClick={() => onAgregarItem(selectedItem)}
            startIcon={<AddShoppingCartIcon />}
            sx={{
              height: 42,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: selectedItem ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
            }}
          >
            Agregar
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Descripción Extendida / Notas (Opcional)"
            placeholder="Ej: vacuna rabia colocada por cumplimiento lote 13254"
            value={itemForm.observaciones}
            onChange={(e) => setItemForm((prev) => ({ ...prev, observaciones: e.target.value }))}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AlbaranItemSelector;